import { Link } from "react-router-dom";
import { ArrowRight, Users, GraduationCap, FileSpreadsheet, TrafficCone } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";


function Feature({ icon: Icon, title, children, testId }) {
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-6" data-testid={testId}>
      <Icon className="w-5 h-5 text-stone-700" />
      <div className="font-editorial text-xl mt-3">{title}</div>
      <p className="text-sm text-stone-600 mt-2 leading-relaxed">{children}</p>
    </div>
  );
}


export default function CounsellorsLanding() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="WUA for Counsellors · Advise your whole class in the time it takes to advise one student"
        description="Cohort manager for school counsellors and independent education consultants. Auto-shortlists per student, class-wide comparison table, entry-realism traffic lights, CSV export. Free tier up to 15 students."
        image="/og/counsellors.png"
        path="/counsellors"
      />

      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="counsellors-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">WUA for Counsellors · Free tier</div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Advise your whole class in the time it takes to advise <em>one student.</em>
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            Add every student in your cohort, get an AI-matched shortlist for each, and see one grid showing which
            course each student should be firming — with entry-realism traffic lights and one-click CSV export
            for parent meetings.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/counsellors/dashboard"
              data-testid="counsellors-cta-start"
              className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-5 py-3 rounded-md text-sm"
            >
              Start free · up to 15 students <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-3 text-xs text-stone-500 italic">
            No credit card. Self-declaration at sign-in — flip yourself into counsellor mode from the dashboard.
          </p>
        </div>

        <aside className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6" data-testid="counsellors-free-card">
          <div className="label-uppercase !text-[10px] text-stone-500">Free tier</div>
          <div className="font-editorial text-5xl mt-2">£0</div>
          <ul className="mt-4 space-y-2 text-sm text-stone-700">
            <li>· Up to 15 students total</li>
            <li>· Unlimited cohorts</li>
            <li>· Auto-shortlists via WUA matching engine</li>
            <li>· CSV export for parent meetings</li>
            <li>· Entry-realism traffic lights</li>
          </ul>
          <div className="mt-6 pt-4 border-t border-stone-200 text-xs text-stone-500">
            Larger cohorts? Contact us for a school licence with unlimited students and shared branding.
          </div>
        </aside>
      </div>

      {/* FEATURES */}
      <section className="mt-20" data-testid="counsellors-features">
        <div className="label-uppercase text-stone-500">Tier 1 · shipping this month</div>
        <h2 className="font-editorial text-3xl sm:text-4xl mt-2">
          Everything a counsellor asks for in the first meeting.
        </h2>

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Feature icon={Users} title="Cohort Manager" testId="feature-cohort-manager">
            Add every student, their predicted grades, subjects, budget and passport. Group by year, department
            or intake. Notes field for anything WUA doesn't capture.
          </Feature>
          <Feature icon={GraduationCap} title="Auto-Shortlists" testId="feature-auto-shortlists">
            One click per student generates 6 WUA-matched courses across UK, US, EU and beyond — using the
            same 5-layer engine that powers Advanced Search.
          </Feature>
          <Feature icon={TrafficCone} title="Entry-Realism Traffic Lights" testId="feature-realism">
            Every shortlisted course is tagged 🟢 Safe · 🟡 Match · 🔴 Reach against each student's grades. The
            #1 thing counsellors eyeball manually today — now automated.
          </Feature>
          <Feature icon={FileSpreadsheet} title="Class Comparison + CSV" testId="feature-csv">
            One grid showing every student × their top 3 courses with salary, cost, country. One-click CSV
            export for parent meetings or head-of-year reviews.
          </Feature>
        </div>
      </section>

      {/* FUTURE */}
      <section className="mt-20 border-t border-stone-200 pt-10" data-testid="counsellors-roadmap">
        <div className="label-uppercase text-stone-500">On the roadmap</div>
        <h3 className="font-editorial text-2xl mt-2">
          Deadline tracker · Cohort insights · Branded parent reports · School-level accounts
        </h3>
        <p className="text-sm text-stone-600 mt-3 max-w-2xl">
          Tier 2 lands as soon as we have 10 counsellors on Tier 1. Tell us what would be most useful — we
          ship what real counsellors ask for.
        </p>
      </section>

      {/* ENTERPRISE */}
      <section className="mt-20 rounded-lg border border-stone-300 bg-[#0F1420] text-stone-100 p-8 md:p-10" data-testid="counsellors-enterprise">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-2">
            <div className="label-uppercase !text-stone-400">For schools & consultancies</div>
            <h2 className="font-editorial text-3xl sm:text-4xl mt-2">
              Running a whole year group, or advising 50+ students privately?
            </h2>
            <p className="mt-4 text-sm text-stone-300 leading-relaxed max-w-2xl">
              WUA Enterprise gives you unlimited students, school-level shared cohorts, branded parent PDFs, an authoritative
              REST API for MIS-system integration (SIMS, Bromcom, iSAMS, Salesforce), and priority support.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-stone-200">
              <li className="flex items-start gap-2"><span className="text-stone-500">·</span> Unlimited students &amp; cohorts across your team</li>
              <li className="flex items-start gap-2"><span className="text-stone-500">·</span> REST API + webhooks for MIS + CRM sync</li>
              <li className="flex items-start gap-2"><span className="text-stone-500">·</span> School-branded parent-facing PDF reports</li>
              <li className="flex items-start gap-2"><span className="text-stone-500">·</span> SSO via Google Workspace or Microsoft 365</li>
              <li className="flex items-start gap-2"><span className="text-stone-500">·</span> Dedicated onboarding, GDPR data-processing agreement</li>
            </ul>
          </div>
          <div className="rounded-md bg-white/5 border border-white/10 p-5">
            <div className="label-uppercase !text-stone-400 !text-[10px]">Get in touch</div>
            <div className="font-editorial text-xl mt-1">Enterprise pricing on request</div>
            <p className="mt-2 text-xs text-stone-300 leading-relaxed">
              We usually reply within one working day. Tell us your school / consultancy size and current tooling.
            </p>
            <a
              href="mailto:enterprise@whichuniversityadvisor.com?subject=WUA Counsellor Tools · Enterprise enquiry"
              data-testid="counsellors-enterprise-cta"
              className="mt-4 inline-flex items-center gap-2 bg-white text-stone-900 hover:bg-stone-100 px-4 py-2.5 rounded-md text-sm font-medium"
            >
              enterprise@whichuniversityadvisor.com
            </a>
          </div>
        </div>
      </section>

      <div
        className="mt-16 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
        data-testid="counsellors-footer-cta"
      >
        <div>
          <h3 className="font-editorial text-2xl">Ready to add your first cohort?</h3>
          <p className="text-sm text-stone-600 mt-1">Takes ~90 seconds to set up.</p>
        </div>
        <Link
          to="/counsellors/dashboard"
          data-testid="counsellors-cta-footer"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Open Counsellor Dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
